import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Clock3,
  Info,
  Loader2,
  Mail,
  Send,
  TriangleAlert,
} from "lucide-react";
import { toast } from "sonner";
import { adminPlatformSettingService } from "@/services/admin-platform-setting.service";
import { type PlatformSettingListDto } from "@/types/platform-setting";
import {
  smtpSettingsSchema,
  sendTestEmailSchema,
  type SmtpSettingsFormData,
} from "@/schemas/smtp-settings";
import {
  SettingsField,
  SettingsFormFooter,
  SettingsFormSkeleton,
  SettingsLoadError,
  SettingsSection,
  SettingsSwitch,
} from "./SettingsForm";

const TYPE_TO_FIELD: Record<string, keyof SmtpSettingsFormData> = {
  SmtpHost: "smtpHost",
  SmtpPort: "smtpPort",
  SmtpUsername: "smtpUsername",
  SmtpPassword: "smtpPassword",
  SmtpFromEmail: "smtpFromEmail",
  SmtpFromName: "smtpFromName",
  SmtpUseSsl: "smtpUseSsl",
};

function settingsToForm(
  settings: Record<string, PlatformSettingListDto>,
): SmtpSettingsFormData {
  return {
    smtpHost: settings.SmtpHost?.value ?? "",
    smtpPort: settings.SmtpPort?.value ?? "587",
    smtpUsername: settings.SmtpUsername?.value ?? "",
    smtpPassword: "",
    smtpFromEmail: settings.SmtpFromEmail?.value ?? "",
    smtpFromName: settings.SmtpFromName?.value ?? "WiCloud",
    smtpUseSsl: settings.SmtpUseSsl?.value !== "false",
  };
}

export function SmtpSettingsTab() {
  const queryClient = useQueryClient();
  const [testEmail, setTestEmail] = useState("");

  const {
    data: smtpSettings,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["admin", "platform-settings", "smtp"],
    queryFn: () => adminPlatformSettingService.getSmtpSettings(),
  });

  const form = useForm<SmtpSettingsFormData>({
    resolver: zodResolver(smtpSettingsSchema),
    defaultValues: {
      smtpHost: "",
      smtpPort: "587",
      smtpUsername: "",
      smtpPassword: "",
      smtpFromEmail: "",
      smtpFromName: "WiCloud",
      smtpUseSsl: true,
    },
  });

  useEffect(() => {
    if (smtpSettings) {
      form.reset(settingsToForm(smtpSettings));
    }
  }, [smtpSettings, form]);

  const updateMutation = useMutation({
    mutationFn: async (data: SmtpSettingsFormData) => {
      const changes: { id: string; value: string }[] = [];
      const missing: string[] = [];

      for (const [type, field] of Object.entries(TYPE_TO_FIELD)) {
        const setting = smtpSettings?.[type];
        const newValue = String(data[field]);

        // Skip password if left empty — preserve existing value
        if (field === "smtpPassword" && !newValue) continue;

        if (!setting) {
          // This screen only updates rows, so an edit to a field whose row does not exist would
          // otherwise be dropped while the form kept showing it as unsaved.
          if (form.getFieldState(field).isDirty) missing.push(type);
          continue;
        }

        if (setting.value !== newValue) {
          changes.push({ id: setting.id, value: newValue });
        }
      }

      // Checked before any request goes out, so a refused save never lands half of the form.
      if (missing.length > 0) {
        throw new Error(
          `No stored setting for ${missing.join(", ")}. Add it in the General tab first.`,
        );
      }

      if (changes.length === 0) {
        return 0;
      }

      const results = await Promise.allSettled(
        changes.map((change) =>
          adminPlatformSettingService.updateSetting(change.id, {
            value: change.value,
          }),
        ),
      );
      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length > 0) {
        throw new Error(`${failed.length} setting(s) failed to update`);
      }

      return changes.length;
    },
    onSuccess: (count) => {
      if (count === 0) {
        toast.info("No changes to save");
        return;
      }
      queryClient.invalidateQueries({
        queryKey: ["admin", "platform-settings"],
      });
      toast.success(`${count} SMTP setting(s) updated`);
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const testEmailMutation = useMutation({
    mutationFn: (email: string) =>
      adminPlatformSettingService.sendTestEmail(email),
    onSuccess: (message) => {
      toast.success(message);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleTestEmail = () => {
    const result = sendTestEmailSchema.safeParse({ email: testEmail.trim() });
    if (!result.success) {
      toast.error(result.error.issues[0].message);
      return;
    }
    testEmailMutation.mutate(testEmail.trim());
  };

  const { errors, isDirty } = form.formState;
  const useSsl = form.watch("smtpUseSsl");
  // The row can exist with an empty value, which is not a password the worker can sign in with.
  const hasPassword = !!smtpSettings?.SmtpPassword?.value;

  return (
    // The form on the left, the test beside it: in one full-width column every field stretched to
    // ~800px, a three-digit port included, and the SSL switch sat a screen away from its label.
    <div className="grid items-start gap-5 2xl:grid-cols-[minmax(0,1fr)_360px]">
      <Card className="overflow-hidden shadow-sm">
        <CardHeader className="border-b bg-muted/30 p-5">
          <CardTitle className="flex items-center gap-2">
            <span className="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Mail className="size-4" />
            </span>
            SMTP Configuration
          </CardTitle>
          <CardDescription className="max-w-2xl leading-6">
            The mail server every platform email is sent through.
          </CardDescription>
        </CardHeader>

        {isLoading ? (
          <SettingsFormSkeleton rows={5} />
        ) : isError ? (
          <SettingsLoadError onRetry={() => void refetch()} />
        ) : (
          <form
            onSubmit={form.handleSubmit(
              (data) => updateMutation.mutate(data),
              (formErrors) => {
                const firstError = Object.values(formErrors)[0];
                if (firstError?.message) {
                  toast.error(firstError.message);
                }
              },
            )}
          >
            <CardContent className="divide-y p-0">
              <SettingsSection
                title="Server"
                description="Where the mail worker connects."
              >
                <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_120px]">
                  <SettingsField
                    id="smtpHost"
                    label="Host"
                    error={errors.smtpHost?.message}
                  >
                    <Input
                      id="smtpHost"
                      placeholder="smtp.gmail.com"
                      autoComplete="off"
                      {...form.register("smtpHost")}
                    />
                  </SettingsField>
                  <SettingsField
                    id="smtpPort"
                    label="Port"
                    error={errors.smtpPort?.message}
                  >
                    <Input
                      id="smtpPort"
                      inputMode="numeric"
                      placeholder="587"
                      {...form.register("smtpPort")}
                    />
                  </SettingsField>
                </div>

                {/* On, the worker connects with MailKit's SecureSocketOptions.Auto, which picks
                    the TLS mode from the port; off, SecureSocketOptions.None. */}
                <SettingsSwitch
                  id="smtpUseSsl"
                  label="Use SSL/TLS"
                  description={
                    useSsl
                      ? "Implicit TLS on port 465; on other ports, STARTTLS as the server allows."
                      : "Not encrypted. The password and every message travel in plain text."
                  }
                  checked={useSsl}
                  onCheckedChange={(checked) =>
                    form.setValue("smtpUseSsl", checked, { shouldDirty: true })
                  }
                />
              </SettingsSection>

              <SettingsSection
                title="Authentication"
                description="The account the worker signs in with."
              >
                <div className="grid gap-4 md:grid-cols-2">
                  <SettingsField
                    id="smtpUsername"
                    label="Username"
                    error={errors.smtpUsername?.message}
                  >
                    <Input
                      id="smtpUsername"
                      placeholder="user@example.com"
                      autoComplete="off"
                      {...form.register("smtpUsername")}
                    />
                  </SettingsField>
                  <SettingsField
                    id="smtpPassword"
                    label="Password"
                    hint={
                      hasPassword
                        ? "A password is set. Leave empty to keep it."
                        : undefined
                    }
                  >
                    <Input
                      id="smtpPassword"
                      type="password"
                      placeholder={hasPassword ? "Unchanged" : "Password"}
                      autoComplete="new-password"
                      {...form.register("smtpPassword")}
                    />
                  </SettingsField>
                </div>
              </SettingsSection>

              <SettingsSection
                title="Sender"
                description="What recipients see in the From line."
              >
                <div className="grid gap-4 md:grid-cols-2">
                  <SettingsField
                    id="smtpFromEmail"
                    label="From email"
                    error={errors.smtpFromEmail?.message}
                  >
                    <Input
                      id="smtpFromEmail"
                      type="email"
                      placeholder="noreply@example.com"
                      {...form.register("smtpFromEmail")}
                    />
                  </SettingsField>
                  <SettingsField
                    id="smtpFromName"
                    label="From name"
                    error={errors.smtpFromName?.message}
                  >
                    <Input
                      id="smtpFromName"
                      placeholder="WiCloud"
                      {...form.register("smtpFromName")}
                    />
                  </SettingsField>
                </div>
              </SettingsSection>
            </CardContent>

            <SettingsFormFooter
              isDirty={isDirty}
              isSaving={updateMutation.isPending}
              onDiscard={() =>
                form.reset(smtpSettings ? settingsToForm(smtpSettings) : undefined)
              }
            />
          </form>
        )}
      </Card>

      <Card className="shadow-sm">
        <CardHeader className="p-5 pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Send className="size-4 text-primary" /> Test Email
          </CardTitle>
          <CardDescription>
            Confirm the saved configuration can deliver.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 p-5 pt-0">
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              handleTestEmail();
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="smtpTestRecipient">Recipient</Label>
              <Input
                id="smtpTestRecipient"
                type="email"
                placeholder="recipient@example.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={testEmailMutation.isPending || !testEmail.trim()}
            >
              {testEmailMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" /> Send test email
                </>
              )}
            </Button>
          </form>

          {/* The test is queued for the mail worker, which reads the settings from the database,
              never from this form. */}
          {isDirty && (
            <p className="flex gap-2 rounded-md border border-warn/25 bg-warn-soft px-3 py-2 text-xs text-warn-ink">
              <TriangleAlert className="mt-px size-3.5 shrink-0" />
              Unsaved changes are not used. Save first to test them.
            </p>
          )}

          <ul className="space-y-2 text-xs text-muted-foreground">
            <li className="flex gap-2">
              <Clock3 className="mt-px size-3.5 shrink-0" />
              Delivery may take a few moments.
            </li>
            {/* DynamicSmtpSettingsProvider (WiCloud.Worker) caches the settings for 5 minutes. */}
            <li className="flex gap-2">
              <Info className="mt-px size-3.5 shrink-0" />
              The mail worker picks up saved changes within 5 minutes, so a test sent sooner may
              still use the previous server.
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
